"use client"

import React, { useState, useEffect, useRef } from "react";
import InterviewCard, { MainTimer } from "./interviewchard";
import ChatBox from "./chatcard";
import { ConversationMode, InterViewCurrentTask, InterviewInitState } from "../../types/interview.type";
import { useInterview } from "../../contexts/interview.context";
import CountdownModal from "./CountDown";
import ConfirmationDialog from "./CloseConfirm";
import { useNavigate } from "react-router-dom";
import StartInterviewDialog from "./StartInterviewDialog";
import { AlertTriangle, TriangleAlert } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ScoreReport from "./InterviewScoreReport";
import CountdownModalToRedirect from "../home/CountDownModal";
import { SessionStorage } from "../../types/general.type";
import { MdCallEnd } from "react-icons/md";
import VideoControllers from "./VideoControllers";
import MessageInput from "./messageInput";
import { useTheme } from "../../context";
import { IoClose } from "react-icons/io5";

const Interview: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [showChat, setShowChat] = useState(false);
  const [openStartModal, setOpenStartModal] = useState(false);
  const [countDownOpened, setCountDownOpened] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showScore, setShowScore] = useState(false);
  const [outOfScopeCount, setOutOfScopeCount] = useState(0);
  const [outOfScopeWarning, setOutOfScopeWarning] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [openCountdownModal, setOpenCountdownModal] = useState(false);
  const [displayOutOfScopeWarning, setDisplayOutOfScopeWarning] = useState(false);

  // const location = useLocation();
  const state = JSON.parse(sessionStorage.getItem(SessionStorage.INTERVIEW_STATE) || "{}") as InterviewInitState;
  const interviewRef = useRef<HTMLDivElement | null>(null);

  const { interview, updateInterview, mutationRef, recognition, resetInterview, startInterview } = useInterview();
  const { setUp, isFullScreen, audioRef, recordingStarted } = interview;
  const withSound = state?.setup?.conversationMode !== ConversationMode.CHAT;

  const toggleChat = () => {
    setShowChat((prev) => !prev);
  };

  useEffect(() => {
    // if (withSound) {
    setOpenStartModal(true);
    // }
    // else {
    //   onInterviewStart();
    // }
  }, [withSound]);

  useEffect(() => {
    if (!countDownOpened) return;
    const timer = setTimeout(() => {
      startInterview();
      setCountDownOpened(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [countDownOpened, startInterview]);

  const enterFullScreen = () => {
    if (!interviewRef.current) return;
    const el = interviewRef.current;
    if (el.requestFullscreen) el.requestFullscreen();
    else if ((el as any).webkitRequestFullscreen) (el as any).webkitRequestFullscreen();
    else if ((el as any).msRequestFullscreen) (el as any).msRequestFullscreen();
  };

  const exitFullScreen = () => {
    if (document.exitFullscreen) document.exitFullscreen();
    else if ((document as any).webkitExitFullscreen) (document as any).webkitExitFullscreen();
    else if ((document as any).msExitFullscreen) (document as any).msExitFullscreen();
  };

  useEffect(() => {
    if (isFullScreen) enterFullScreen();
    else if (document.fullscreenElement) exitFullScreen();
  }, [isFullScreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        updateInterview({ isFullScreen: false });
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [updateInterview]);

  const onCancel = () => {
    setOpenConfirmation(false);
    if (recordingStarted) {
      sessionStorage.setItem(SessionStorage.LISTENING, "true");
      try {
        recognition?.start();
      } catch (error) {
      }
    }
    if (audioRef.current?.paused && !audioRef.current.ended) {
      audioRef.current?.play()
      updateInterview({
        currentTask: InterViewCurrentTask.BOT_SPEAKING
      })
    };
  };

  const closeInterview = () => {
    sessionStorage.removeItem(SessionStorage.LISTENING);
    sessionStorage.removeItem(SessionStorage.INTERVIEW_STATE);
    resetInterview();
    navigate("/interview-setup");
  };

  const onInterviewStart = () => {
    setOpenStartModal(false);
    setCountDownOpened(true);
  };



  useEffect(() => {
    if (setUp?.completed) {
      setShowModal(true);
    }
  }, [setUp]);

  useEffect(() => {
    const shouldWarn = interview.interviewData?.isUserResponseOutOfScope;
    if (!shouldWarn) return;

    setOutOfScopeCount((prev) => {
      const newCount = prev + 1;
      if (newCount < 4) {
        setOutOfScopeWarning(true);
        // setTimeout(() => setOutOfScopeWarning(false), 3000);
      } else {
        resetInterview();
        setOpenCountdownModal(true);
      }
      return newCount;
    });
  }, [interview.interviewData]);



  return (
    <div
      className={`flex flex-col items-center  justify-stretch   mobile:justify-end tablet:justify-end xl:px-6 px-0 w-full h-full tablet:h-full overflow-hidden transition-all duration-300 dark:bg-[rgba(35,34,38,1)] bg-[rgba(247,248,250,1)]`}
    >


      {/* Main Content */}
      <div
        ref={interviewRef}
        className={`flex flex-col   !touch-auto max-h-90-vh-safe mobile:max-h-78-vh-safe tablet:max-h-90-vh-safe box-border  justify-items-center items-center h-full w-[96%] gap-y-2 py-1 xl:w-[96%] md:${withSound ? "grid " : "flex-col"
          } gap-x-2 grid-cols-[1fr_1fr]  sm:grid-cols-[60%_40%] grid-rows-[2.6rem_9fr_1fr] w-full flex-1 transition-all duration-300 justify-start items-stretch sm:px-14 px-3 ${isFullScreen ? "items-center justify-center  md:p-10 xl:p-15" : "md:justify-center "
          } dark:bg-[rgba(35,34,38,1)] bg-[rgba(247,248,250,1)] rounded-lg `}
      >
        {/* Out-of-Scope Warning */}
        <AnimatePresence>
          {displayOutOfScopeWarning && (
            <motion.div
              key="oos-warning"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className={`fixed top-10 right-5 z-[999999] px-6 py-4 rounded-xl flex items-start space-x-3 bg-orange-400/90 border-l-red-500 border-l-4 text-gray-100 shadow-[0_4px_6px_rgba(0,0,0,0.1),_0_1px_3px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_6px_rgba(0,0,0,0.3),_0_1px_3px_rgba(0,0,0,0.2)]`}
            >
              <AlertTriangle className="w-6 h-6 flex-shrink-0 text-white" />
              <div>
                <p className="font-semibold">Please stay on topic</p>
                <p className="text-sm">This is your warning #{outOfScopeCount}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {/* Completion Modal */}
        {showModal && (
          <div className="fixed inset-0  bg-black bg-opacity-50 flex items-center justify-center z-[2100]">
            <div
              className={`p-6 rounded-xl w-[90%] sm:w-96 relative mt-12 transition-all duration-200 dark:bg-[rgba(35,34,38,1)] bg-white  border-accent-700/40 border shadow-[0_4px_6px_rgba(0,0,0,0.1),_0_1px_3px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_6px_rgba(0,0,0,0.3),_0_1px_3px_rgba(0,0,0,0.2)]`}
            >
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Congratulations! 🎉</h2>
              <p className="mb-4 text-gray-600 dark:text-gray-300">Well done on completing your interview!</p>
              {!showScore ? (
                <button
                  onClick={() => {
                    const report = JSON.stringify(interview.interviewData?.scoreReport)
                    sessionStorage.setItem(SessionStorage.SCORE_REPORT, report)
                    sessionStorage.setItem(SessionStorage.OVERALL_REPORT, interview.interviewData?.overallReport || "N/A")
                    navigate(`/ended-interview-score?isFullScreen=${isFullScreen}&jobTitle=${interview.interviewInitResponse?.jobTitle}&seniority=${interview.interviewInitResponse?.seniorityLevel || ''}`);

                  }}
                  className={`px-4 py-2 rounded-lg text-white font-medium transition-all duration-200 dark:bg-gradient-to-r dark:from-teal-600 dark:to-teal-700 dark:hover:from-teal-700 dark:hover:to-teal-800
                    bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 shadow-[0_2px_4px_rgba(0,0,0,0.1)] hover:shadow-[0_3px_6px_rgba(0,0,0,0.15)]`}
                >
                  Show Score
                </button>
              ) : (
                <ScoreReport />
              )}
              <button
                onClick={() => {
                  setShowModal(false);
                  setShowScore(false);
                  closeInterview();
                }}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {withSound && (
          <div className=" col-span-2 max-h-[2.6rem]  flex items-center   w-full">
            <SubHeader
              outOfScopeCount={outOfScopeCount}
              outScopeWarning={outOfScopeWarning}
              displayOutofScope={() => {
                if (displayOutOfScopeWarning) return
                setDisplayOutOfScopeWarning(true);
                setTimeout(() => {
                  setDisplayOutOfScopeWarning(false);
                }, 3000);
              }
              }
              onClose={() => {
                setOpenConfirmation(true);
              }}
              withMargin={true}
            />
          </div>
        )}
        {withSound && (
          <div className="w-full  h-full lg:h-full md:max-h-[calc(100vh-8rem-7vh)] flex-grow">
            <InterviewCard
              handleClose={() => {
                sessionStorage.removeItem(SessionStorage.LISTENING);
                mutationRef.current?.abort();
                audioRef.current?.played && audioRef.current?.pause();
                updateInterview({ isFullScreen: false, currentTask: InterViewCurrentTask.TRYING_TO_STOP_INTERVIEW });
                setOpenConfirmation(true);
              }}
              showChat={showChat}
              toggleChat={toggleChat}
            />
          </div>
        )}

        {withSound && (
          <div className="hidden md:flex w-full max-h-[calc(100vh-8rem-7vh)]  md:max-h-full flex-grow flex-col items-center justify-start h-full">
            <ChatBox toggleChat={toggleChat} />
          </div>
        )}

        {!withSound && (
          <div className=" gap-y-1 !touch-auto  box-border  w-full min-h-90-vh-safe max-h-90-vh-safe mobile:min-h-77-vh-safe mobile:max-h-77-vh-safes tablet:min-h-90-vh-safe tablet:max-h-90-vh-safe grid grid-rows-[2.5rem_1fr_4.5rem]  px-0 sm:px-5 md:px-10  grid-cols-1">
            <SubHeader
              outOfScopeCount={outOfScopeCount}
              outScopeWarning={outOfScopeWarning}
              displayOutofScope={() => {
                if (displayOutOfScopeWarning) return
                setDisplayOutOfScopeWarning(true);
                setTimeout(() => {
                  setDisplayOutOfScopeWarning(false);
                }, 3000);
              }
              }
              withMargin={false}
              onClose={() => {
                setOpenConfirmation(true);
              }}
            />
            <div className="flex overflow-hidden flex-col items-center  justify-stretch  ">
              <ChatBox toggleChat={toggleChat} />
            </div>
            <div className="flex  p-0 h-full w-full items-stretch justify-center">
              <MessageInput />
            </div>
          </div>
        )}

        {showChat && withSound && (
          <div
            onClick={() => setShowChat(false)}
            className="md:hidden fixed z-[2000] top-0 pb-1 left-0 w-screen h-100-vh-safe backdrop-blur-md flex items-center justify-center"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className={`relative w-full max-h-[97%]  h-[97%] min-h-[97%] px-2 pb-1  box-border pt-6 py-0 gap-y-1  grid grid-rows-[1fr_4.5rem] ${theme === "dark"
                ? "dark:bg-[rgba(35,34,38,1)] dark:border-accent-700/40 border"
                : "bg-[rgba(247,248,250,1)]  border-accent-700/40 border"
                } rounded-xl shadow-[0_4px_6px_rgba(0,0,0,0.1),_0_1px_3px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_6px_rgba(0,0,0,0.3),_0_1px_3px_rgba(0,0,0,0.2)]`}
            >
              <button
                className={` absolute bg-transparent left-2 top-1 text-slate-700 dark:text-slate-200 z-[5000]  `}
                onClick={toggleChat}
              >
                <IoClose />
              </button>

              <div className="flex overflow-hidden flex-col items-center  justify-stretch  ">
                <ChatBox toggleChat={toggleChat} />
              </div>
              <div className="flex  p-0 h-full w-full items-stretch justify-center">
                <MessageInput />
              </div>
            </div>
          </div>
        )}
        {withSound && (
          <VideoControllers
            showChat={showChat}
            toggleChat={toggleChat}
            handleClose={() => {
              sessionStorage.removeItem(SessionStorage.LISTENING);
              mutationRef.current?.abort();
              audioRef.current?.played && audioRef.current?.pause();
              updateInterview({ isFullScreen: false, currentTask: InterViewCurrentTask.TRYING_TO_STOP_INTERVIEW });
              setOpenConfirmation(true);
            }}
          />
        )}
        {withSound && (
          <div
            className={`hidden md:flex  min-h-[3rem] items-start   justify-center w-[100%]`}
          >
            <MessageInput />
          </div>
        )}
      </div>

      {/* Modals & Dialogs */}
      <CountdownModal isOpen={countDownOpened} />
      <ConfirmationDialog
        isOpen={openConfirmation}
        onClose={onCancel}
        onConfirm={closeInterview}
        message="Are you sure you want to end the interview?"
      />
      <StartInterviewDialog
        isOpen={openStartModal}
        onClose={() => {
          closeInterview();
          setOpenStartModal(false);
        }}
        onConfirm={onInterviewStart}
        message="The interview session is ready. Click the 'Start Interview' button when you are ready."
      />
      {openCountdownModal && <CountdownModalToRedirect redirectTo="/interview-setup" />}
    </div>
  );
};

interface SubHeaderProps {
  onClose: () => void;
  withMargin?: boolean;
  outScopeWarning: boolean;
  displayOutofScope: () => void;
  outOfScopeCount: number;
}

const SubHeader = ({ onClose, withMargin = true, outOfScopeCount, outScopeWarning, displayOutofScope }: SubHeaderProps) => {
  const { interview } = useInterview();
  const { setUp, interviewInitResponse } = interview;
  const { theme } = useTheme();
  const [iconSize, setIconSize] = useState(window.innerWidth < 440 ? 16 : window.innerWidth < 768 ? 20 : 24);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const isChatMode = setUp?.conversationMode === ConversationMode.CHAT;

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenWidth(width);
      setIconSize(width < 440 ? 16 : width < 768 ? 20 : 24);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const buttonSize = screenWidth < 440 ? "w-9 h-9" : screenWidth < 768 ? "w-10 h-10" : "w-12 h-12";

  return (

    <div
      className={`w-full rounded-lg min-h-full flex flex-nowrap items-center justify-between px-2 py-[1vh] gap-1 transition-all duration-200 ${theme === "dark"
        ? "bg-[rgba(46,45,50,1)] border-accent-700/40 border"
        : "bg-white  border-accent-700/40 border"
        } shadow-[0_4px_6px_rgba(0,0,0,0.1),_0_1px_3px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_6px_rgba(0,0,0,0.3),_0_1px_3px_rgba(0,0,0,0.2)]`}
    >
      <div className="flex items-center  gap-1 min-w-0">
        <MainTimer />
      </div>
      {interviewInitResponse?.jobTitle && (
        <div
          className={`px-2 py-1 flex-1 flex items-center justify-center text-[10px] xs:text-xs font-semibold truncate min-w-0 ${theme === "dark" ? "text-teal-400" : "text-teal-500"
            }`}
        >
          <span>Position:</span> {interviewInitResponse.jobTitle}
        </div>
      )}
      {
        outScopeWarning && (
          <button
            onClick={() => {
              displayOutofScope();
            }}
            className={`px-2 py-1 rounded-md text-[10px] flex items-center gap-1 text-red-500 xs:text-xs font-semibold truncate min-w-0 ${theme === "dark" ? "bg-red-300/40" : "bg-red-300/70"
              }`}
          >
            <TriangleAlert />
            <span className="text-red-500">Out of scope #{outOfScopeCount}</span>
          </button>
        )
      }



      {isChatMode && (
        <button
          onClick={onClose}
          className={`h-10 w-10 bg-red-500 hover:bg-red-600 rounded-full text-white  flex justify-center items-center gap-1.5 transition-all duration-200 ${theme === "dark"
            ? "border-accent-700/40 border"
            : " border-accent-700/40 border"
            } shadow-[0_2px_4px_rgba(0,0,0,0.1)] hover:shadow-[0_3px_6px_rgba(0,0,0,0.15)]`}
        >
          <MdCallEnd size={20} />
        </button>
      )}
    </div>
  );
};

export default Interview;